/*
 * Makerz custom cart drawer (replaces the paid side-cart app).
 *
 * Keeps the interface the theme's product form already calls:
 *   getSectionsToRender(), renderContents(response), setActiveElement(el), open(), close()
 * so product pages, the featured product section and quick add all open this drawer.
 *
 * Every change (qty, remove, upsell add) posts to the AJAX cart API and asks
 * Shopify to re-render the 'cart-drawer' and 'cart-icon-bubble' sections in the
 * same request, so prices, discounts and the free shipping bar always come from
 * the server (no money formatting in JS).
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.queue = Promise.resolve();
    this.qtyTimers = {};
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  get panel() {
    return this.querySelector('.skc__panel');
  }

  routes() {
    const r = window.routes || {};
    return {
      add: (r.cart_add_url || '/cart/add') + '.js',
      change: (r.cart_change_url || '/cart/change') + '.js',
    };
  }

  /* ---------- header cart icon ---------- */

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
    }
    if (window.__skcIconBound) return;
    window.__skcIconBound = true;
    // Delegated so it survives the header being re-rendered in the theme editor.
    document.addEventListener('click', (event) => {
      const link = event.target.closest('#cart-icon-bubble');
      if (!link) return;
      event.preventDefault();
      this.open(link);
    });
    document.addEventListener('keydown', (event) => {
      const link = event.target.closest && event.target.closest('#cart-icon-bubble');
      if (!link || event.code !== 'Space') return;
      event.preventDefault();
      this.open(link);
    });
  }

  /* ---------- open / close ---------- */

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    document.body.classList.add('overflow-hidden');
    if (this.classList.contains('active')) return;

    this.classList.add('animate');
    requestAnimationFrame(() => this.classList.add('active'));

    let done = false;
    const focusInside = () => {
      if (done) return;
      done = true;
      if (typeof trapFocus === 'function') trapFocus(this.panel, this.querySelector('.skc__close') || this.panel);
    };
    this.panel.addEventListener('transitionend', focusInside, { once: true });
    setTimeout(focusInside, 400); // reduced motion / no transition
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  /* Called by product-form.js and quick add with the /cart/add response. */
  renderContents(parsedState) {
    if (parsedState && parsedState.sections) this.renderSections(parsedState.sections);
    setTimeout(() => this.open());
  }

  renderSections(sections) {
    const drawerHtml = sections['cart-drawer'];
    if (drawerHtml) {
      const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
      const next = doc.querySelector('#CartDrawer');
      const nextRoot = doc.querySelector('cart-drawer');
      const current = this.querySelector('#CartDrawer');

      const scroller = this.querySelector('[data-skc-scroll]');
      const scrollTop = scroller ? scroller.scrollTop : 0;
      const active = document.activeElement;
      const focusId = active && this.contains(active) ? active.dataset.focusId : null;

      if (next && current) current.innerHTML = next.innerHTML;
      if (nextRoot) this.classList.toggle('is-empty', nextRoot.classList.contains('is-empty'));

      const newScroller = this.querySelector('[data-skc-scroll]');
      if (newScroller) newScroller.scrollTop = scrollTop;

      if (this.classList.contains('active') && typeof trapFocus === 'function') {
        const target =
          (focusId && this.querySelector(`[data-focus-id="${CSS.escape(focusId)}"]`)) ||
          this.querySelector('.skc__close');
        trapFocus(this.panel, target);
      }
    }

    const bubbleHtml = sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHtml && bubble) {
      const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      const section = doc.querySelector('.shopify-section');
      bubble.innerHTML = section ? section.innerHTML : doc.body.innerHTML;
    }
  }

  /* ---------- events ---------- */

  onClick(event) {
    const target = event.target;

    if (target.closest('[data-skc-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    const remove = target.closest('[data-skc-remove]');
    if (remove) {
      event.preventDefault();
      this.updateQuantity(remove.dataset.skcRemove, 0);
      return;
    }

    const step = target.closest('[data-skc-qty]');
    if (step) {
      event.preventDefault();
      const key = step.dataset.skcQty;
      const input = this.qtyInput(key);
      if (!input) return;
      const next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.skcDelta, 10));
      input.value = next;
      this.scheduleQuantity(key, next);
      return;
    }

    const add = target.closest('[data-skc-add]');
    if (add) {
      event.preventDefault();
      this.addVariant(add.dataset.skcAdd, add);
      return;
    }

    const checkout = target.closest('[data-skc-checkout]');
    if (checkout) checkout.classList.add('is-loading'); // let the form submit normally
  }

  onChange(event) {
    const select = event.target.closest('[data-skc-up-variant]');
    if (select) {
      const option = select.options[select.selectedIndex];
      const card = select.closest('[data-skc-upsell]');
      const price = card.querySelector('[data-skc-up-price]');
      const compare = price.querySelector('.skc-price__compare');
      price.querySelector('.skc-price__current').textContent = option.dataset.price;
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
      card.querySelector('[data-skc-add]').dataset.skcAdd = option.value;
      return;
    }

    const input = event.target.closest('[data-skc-qty-input]');
    if (input) {
      const value = Math.max(0, parseInt(input.value, 10) || 0);
      this.updateQuantity(input.dataset.skcQtyInput, value);
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.classList.contains('active')) {
      this.close();
      return;
    }
    if (event.key === 'Enter' && event.target.matches('[data-skc-qty-input]')) {
      event.preventDefault();
      event.target.blur(); // triggers change
    }
  }

  qtyInput(key) {
    return this.querySelector(`[data-skc-qty-input="${CSS.escape(key)}"]`);
  }

  /* ---------- cart requests ---------- */

  // Rapid +/- taps are collapsed into one request with the final quantity.
  scheduleQuantity(key, quantity) {
    clearTimeout(this.qtyTimers[key]);
    this.qtyTimers[key] = setTimeout(() => this.updateQuantity(key, quantity), 300);
  }

  updateQuantity(key, quantity) {
    clearTimeout(this.qtyTimers[key]);
    const item = this.querySelector(`.skc-item[data-key="${CSS.escape(key)}"]`);
    if (item) item.classList.add('is-updating');

    return this.enqueue(async () => {
      const json = await this.post(this.routes().change, { id: key, quantity });
      const line = (json.items || []).find((i) => i.key === key);
      if (quantity > 0 && line && line.quantity < quantity) {
        const msg = ((window.cartStrings && window.cartStrings.quantityError) || 'Du kan kun lægge [quantity] af denne vare i kurven.')
          .replace('[quantity]', line.quantity)
          .replace('[antal]', line.quantity);
        this.pendingError = msg;
      }
      return json;
    });
  }

  addVariant(variantId, button) {
    if (!variantId || button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    return this.enqueue(() => this.post(this.routes().add, { items: [{ id: Number(variantId), quantity: 1 }] }));
  }

  enqueue(task) {
    this.queue = this.queue
      .then(task)
      .then((json) => {
        this.renderSections(json.sections || {});
        this.showError(this.pendingError);
        this.pendingError = null;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer' });
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: json }));
      })
      .catch(async (error) => {
        console.error('[cart drawer]', error);
        await this.refresh();
        this.showError(error.cartMessage || (window.cartStrings && window.cartStrings.error));
      });
    return this.queue;
  }

  async post(url, body) {
    const payload = Object.assign({}, body, {
      sections: this.getSectionsToRender().map((s) => s.id).join(','),
      sections_url: window.location.pathname,
    });
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload),
    });
    let json = {};
    try {
      json = await response.json();
    } catch (e) {
      /* non-JSON error page */
    }
    if (!response.ok || json.status) {
      const error = new Error(json.description || json.message || 'Cart request failed');
      error.cartMessage = json.description || json.message;
      throw error;
    }
    return json;
  }

  // Re-sync the drawer with the server (used after an error).
  async refresh() {
    try {
      const ids = this.getSectionsToRender().map((s) => s.id).join(',');
      const response = await fetch(`${window.location.pathname}?sections=${ids}`);
      this.renderSections(await response.json());
    } catch (e) {
      /* ignore */
    }
  }

  showError(message) {
    if (!message) return;
    const el = this.querySelector('[data-skc-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }
}

customElements.define('cart-drawer', CartDrawer);
